import { type AppOptions } from './types';

export const ActionPermission = {
  VIEW: 'view',
  UPDATE: 'update',
  CREATE: 'create',
  STATUS: 'status',
  DELETE: 'delete',
} as const;

export const BULK_PERMISSION_NAME = 'bulk';

export const APP_OPTIONS = [
  'exam-management',
  'user-management',
  'exam-center',
  'reports',
] as const;

export const EXAM_MANAGEMENT_APP_PERMISSIONS = [
  'task',
  'task.questionCreation',
  'task.questionReview',
  'task.qsetReview',
  'task.auditLog',
  'qbank',
  'qbank.dashboard',
  'qbank.question',
  'qbank.structure',
  'qbank.los',
  'qbank.setting',
  'qbank.auditLog',
  'exam',
  'exam.examinationCourse',
  'exam.qconfig',
  'exam.qset',
  'exam.auditLog',
];

export const USER_MANAGEMENT_APP_PERMISSIONS = [
  'setting',
  'setting.user',
  'setting.role',
  'setting.auditLog',
];

export const EXAM_CENTER_APP_PERMISSIONS = [
  'ecAdmin',
  'ecAdmin-examCenter',
  'ecAdmin-examCenter.examCenter',
  'ecAdmin-examCenter.member',
  'ecAdmin-examCenter.examCourse',
  'ecAdmin-examCourse',
  'ecAdmin-examCourse.settings',
  'ecAdmin-examDistribution',
  'ecAdmin-examDistribution.examAssigning',
  'ecAdmin-examDistribution.availableQSets',
  'ecAdmin-location',
  'ecAdmin-location.location',
  'ecAdmin-location.locationUsage',
  'ecAdmin-location.locationRequest',
  'ecAdmin-location.locationFirstTierReviewer',
  'ecAdmin-location.locationSecondTierReviewer',
  'ecAdmin-location.locationCancelation',
  'ecAdmin-location.auditLog',
  'ecAdmin-proctor',
  'ecAdmin-proctor.proctor',
  'ecAdmin-proctor.proctorRequest',
  'ecAdmin-proctor.proctorFirstTierReviewer',
  'ecAdmin-proctor.proctorSecondTierReviewer',
  'ecAdmin-proctor.proctorExpiration',
  'ecAdmin-proctor.auditLog',
  'ecAdmin-questionnaire',
  'ecAdmin-questionnaire.report',
  'ecAdmin-blocklist',
  'ecAdmin-blocklist.blockedUser',
  'ecAdmin-announcement',
  'ecAdmin-announcement.announcement',
  'ecAdmin-setPortal',
  'ecAdmin-setPortal.examResultFile',
  'ec',
  'ec-examCenter',
  'ec-examCenter.examCenter',
  'ec-examCenter.member',
  'ec-examCenter.examCourse',
  'ec-examEvent',
  'ec-examEvent.examEvent',
  'ec-examEvent.examinees',
  'ec-examEvent.results',
  'ec-examEvent.auditLog',
  'ec-location',
  'ec-location.location',
  'ec-location.locationRequest',
  'ec-location.locationCancelation',
  'ec-proctor',
  'ec-proctor.proctor',
  'ec-proctor.proctorRequest',
  'ec-proctor.proctorExpiration',
  'ec-proctoring',
  'ec-proctoring.details',
  'ec-proctoring.registration',
  'ec-proctoring.eKyc',
];

export const REPORT_APP_PERMISSIONS = [
  'report',
  'report.examEventOverviewReport',
  'report.examCourseAnalysisReport',
  'report.qbankItemsAnalysisReport',
  'report.qbankItemsAnalysisYearlyReport',
  'report.qsetUsageStatusReport',
  'report.questionUsageByQsetReport',
  'report.questionnaireReport',
];

export const APP_PERMISSIONS_NAME: Record<string, string> = {
  task: 'Task Management',
  'task.questionCreation': 'Question Creation',
  'task.questionReview': 'Question Review',
  'task.qsetReview': 'QSet Review',
  qbank: 'คลังข้อสอบ',
  'qbank.los': 'LOS',
  'qbank.dashboard': 'Dashboard',
  'qbank.question': 'Question List',
  'qbank.structure': 'Structure',
  'qbank.auditLog': 'Activity Log',
  'qbank.setting': 'Settings',
  exam: 'หลักสูตรทดสอบ',
  'exam.examinationCourse': 'Examination Course',
  'exam.qconfig': 'QConfig',
  'exam.qset': 'QSet',
  'exam.auditLog': 'Activity Log',
  setting: 'User Management',
  'setting.user': 'User',
  'setting.role': 'Role & Permission',
  'setting.auditLog': 'Activity Log',
  ecAdmin: 'Exam Center Admin',
  'ecAdmin-examCenter': 'จัดการศูนย์ทดสอบ',
  'ecAdmin-examCenter.examCenter': 'ข้อมูลศูนย์ทดสอบ',
  'ecAdmin-examCenter.member': 'สมาชิก',
  'ecAdmin-examCenter.examCourse': 'หลักสูตรที่เปิดสอบ',
  'ecAdmin-examCourse': 'จัดการหลักสูตร',
  'ecAdmin-examCourse.settings': 'จัดการและตั้งค่าหลักสูตร',
  'ecAdmin-examDistribution': 'มอบหมายชุดข้อสอบ',
  'ecAdmin-examDistribution.examAssigning': 'รอบสอบทั้งหมด',
  'ecAdmin-examDistribution.availableQSets': 'ชุดข้อสอบที่พร้อมใช้งาน',
  'ecAdmin-location': 'จัดการสถานที่สอบ',
  'ecAdmin-location.location': 'สถานที่สอบทั้งหมด',
  'ecAdmin-location.locationUsage': 'สิทธิ์การใช้งาน',
  'ecAdmin-location.locationRequest': 'รายการยื่นคำขออนุมัติสถานที่สอบ',
  'ecAdmin-location.locationFirstTierReviewer': 'ผู้พิจารณาลำดับที่ 1',
  'ecAdmin-location.locationSecondTierReviewer': 'ผู้พิจารณาลำดับที่ 2',
  'ecAdmin-location.locationCancelation': 'ยกเลิกการใช้งาน',
  'ecAdmin-location.auditLog': 'Activity Log',
  'ecAdmin-proctor': 'จัดการผู้คุมสอบ',
  'ecAdmin-proctor.proctor': 'ผู้คุมสอบทั้งหมด',
  'ecAdmin-proctor.proctorRequest': 'รายการยื่นคำขออนุมัติผู้คุมสอบ',
  'ecAdmin-proctor.proctorFirstTierReviewer': 'ผู้พิจารณาลำดับที่ 1',
  'ecAdmin-proctor.proctorSecondTierReviewer': 'ผู้พิจารณาลำดับที่ 2',
  'ecAdmin-proctor.proctorExpiration': 'ผู้คุมสอบที่หมดสัญญา',
  'ecAdmin-proctor.auditLog': 'Activity Log',
  'ecAdmin-blocklist': 'รายชื่อผู้ไม่มีสิทธิ์สอบ',
  'ecAdmin-blocklist.blockedUser': 'จัดการรายชื่อผู้ไม่มีสิทธิ์สอบ',
  'ecAdmin-questionnaire': 'แบบสอบถาม',
  'ecAdmin-questionnaire.report': 'รายงานแบบสอบถาม',
  'ecAdmin-announcement': 'จัดการประกาศข่าวสาร',
  'ecAdmin-announcement.announcement': 'ประกาศข่าวสาร',
  'ecAdmin-setPortal': 'ไฟล์ผลการสอบ',
  'ecAdmin-setPortal.examResultFile': 'รายการไฟล์ผลการสอบ',
  ec: 'Exam Center',
  'ec-examCenter': 'ข้อมูลศูนย์ทดสอบ',
  'ec-examCenter.examCenter': 'ข้อมูลศูนย์ทดสอบ',
  'ec-examCenter.member': 'สมาชิก',
  'ec-examCenter.examCourse': 'หลักสูตรที่เปิดสอบ',
  'ec-examEvent': 'จัดการตารางสอบ',
  'ec-examEvent.examEvent': 'ข้อมูลรอบการสอบ',
  'ec-examEvent.examinees': 'ข้อมูลการสมัครสอบ',
  'ec-examEvent.results': 'ข้อมูลผลการสอบ',
  'ec-examEvent.auditLog': 'Activity Log',
  'ec-location': 'จัดการสถานที่สอบ',
  'ec-location.location': 'สถานที่สอบทั้งหมด',
  'ec-location.locationRequest': 'รายการยื่นคำขออนุมัติสถานที่สอบ',
  'ec-location.locationCancelation': 'ยกเลิกการใช้งาน',
  'ec-proctor': 'จัดการผู้คุมสอบ',
  'ec-proctor.proctor': 'ผู้คุมสอบทั้งหมด',
  'ec-proctor.proctorRequest': 'รายการยื่นคำขออนุมัติผู้คุมสอบ',
  'ec-proctor.proctorExpiration': 'ผู้คุมสอบที่หมดสัญญา',
  'ec-proctoring': 'จัดการทดสอบ',
  'ec-proctoring.details': 'รายละเอียดการคุมสอบ',
  'ec-proctoring.registration': 'การลงทะเบียนผู้เข้าสอบ',
  'ec-proctoring.eKyc': 'ตรวจสอบการยืนยันตัวตน (e-KYC)',
  report: 'Report',
  'report.examEventOverviewReport': 'รายงานสรุปผลภาพรวมการสอบ',
  'report.examCourseAnalysisReport': 'รายงานการวิเคราะห์ผลการสอบตามหลักสูตร',
  'report.qbankItemsAnalysisReport': 'รายงานวิเคราะห์คุณภาพข้อสอบ: แบบรายข้อ',
  'report.qbankItemsAnalysisYearlyReport': 'รายงานวิเคราะห์คุณภาพข้อสอบ: แบบรายปี',
  'report.qsetUsageStatusReport': 'รายงานการใช้งานชุดข้อสอบ',
  'report.questionUsageByQsetReport': 'รายงานการใช้งานข้อสอบแยกตามชุด',
  'report.questionnaireReport': 'รายงานสรุปผลแบบสอบถามหลังการสอบ',
};

export const APP_PERMISSION_MAP: Record<AppOptions, string[]> = {
  'exam-management': EXAM_MANAGEMENT_APP_PERMISSIONS,
  'user-management': USER_MANAGEMENT_APP_PERMISSIONS,
  'exam-center': EXAM_CENTER_APP_PERMISSIONS,
  reports: REPORT_APP_PERMISSIONS,
};
